import { formatTicketNumberDisplay } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { useEffect, useRef } from "react";
import { LuBell } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { GroupTicketAlert, useTicketingStore } from "../../Models/Ticketing/Ticketing.Store";

interface ToastGroupTicketProps {
  alert: GroupTicketAlert;
  onAnimateOut: () => Promise<void>;
}

const AUTO_DISMISS_MS = 8000;

/**
 * "New ticket assigned to {group} group" arrival toast — bell + message +
 * View (open the ticket) and Accept (claim it). Auto-dismisses if untouched.
 */
export function ToastGroupTicket({ alert, onAnimateOut }: ToastGroupTicketProps) {
  const navigate = useNavigate();
  const acceptTicketById = useTicketingStore((s) => s.acceptTicketById);
  const dismissAlert = useTicketingStore((s) => s.dismissGroupTicketAlert);
  const requestMyInboxView = useTicketingStore((s) => s.requestMyInboxView);
  const dismissed = useRef(false);

  const close = async (): Promise<void> => {
    if (dismissed.current) return;
    dismissed.current = true;
    await onAnimateOut();
    dismissAlert(alert.id);
  };

  // Auto-dismiss if the member doesn't act on it.
  useEffect(() => {
    const timer = setTimeout(() => void close(), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTicket = (): void => navigate(`/inbox/ticket/${formatTicketNumberDisplay(alert.ticketNumber)}`);

  const handleView = async (): Promise<void> => {
    openTicket();
    await close();
  };

  const handleAccept = async (): Promise<void> => {
    const claimed = await acceptTicketById(alert.ticketId, alert.version);
    if (claimed) {
      // The ticket is now mine — select My Inbox in the nav and open its detail.
      requestMyInboxView();
      openTicket();
    }
    await close();
  };

  return (
    <>
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <LuBell className="h-4 w-4 shrink-0 text-black" />
        <p className="body-sm-alt min-w-0 flex-1 text-black">New ticket assigned to {alert.groupName} group</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant={ButtonVariant.GHOST} size={ButtonSize.SMALL} onClickAsync={handleView}>
          View
        </Button>
        <Button color={ButtonColor.BLACK} size={ButtonSize.SMALL} onClickAsync={handleAccept}>
          Accept
        </Button>
      </div>
    </>
  );
}
